import { useQuery } from '@tanstack/react-query';
import { filesApi } from '../../api/endpoints';
import type { FileLimits, Usage } from '../../api/types';

/**
 * How full the company's storage is, and whether anything can be uploaded at all. One query for the
 * sidebar meter, the banner and every upload button, so they never disagree. Asked again every
 * minute, and after every upload (uploads.tsx invalidates it), so a company that fills up - or
 * frees space - sees it without reloading.
 */
export function useStorage(tenantId: string | null | undefined) {
  return useQuery({ ...storageQuery(tenantId ?? ''), enabled: Boolean(tenantId), refetchInterval: 60_000 });
}

/** The same query, for code that must wait for the answer (an upload started before it came). */
export const storageQuery = (tenantId: string) => ({ queryKey: ['usage', tenantId], queryFn: () => filesApi.usage(tenantId), staleTime: 30_000 });

/** "Report.Final.PDF" -> "pdf"; no extension -> "". The files service reads names the same way. */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Splits chosen files into those the plan allows and those it does not - by kind, then by size - so
 * nothing is sent that the files service would refuse. The service checks again: this is for the
 * person, not the rule.
 */
export function sortByPolicy<T extends { name: string; size: number }>(files: T[], limits: FileLimits | undefined) {
  const allowed: T[] = [];
  const wrongType: T[] = [];
  const tooLarge: T[] = [];
  for (const file of files) {
    if (limits && !limits.types.includes(extensionOf(file.name))) wrongType.push(file);
    else if (limits && file.size > limits.maxBytes) tooLarge.push(file);
    else allowed.push(file);
  }
  return { allowed, wrongType, tooLarge };
}

/** For the file picker: only what the plan allows is offered. */
export const acceptOf = (limits: FileLimits | undefined) => (limits ? limits.types.map((type) => `.${type}`).join(',') : undefined);

/** Why uploading is not possible right now; null when it is (or nothing is known yet). */
export function uploadBlock(usage: Usage | undefined): 'full' | 'unavailable' | null {
  return usage && !usage.uploads.allowed ? usage.uploads.reason : null;
}
